'use client'

import { useAuthorization } from '@entities/authentication'
import { couponEntityQueries } from '@entities/coupon/api'
import { couponEntityMutations } from '@entities/coupon/api/coupon.mutation'
import type { KrPaymentOrder } from '@entities/kr-payment/model'
import { paymentEntityQueries } from '@entities/payment/api/payment.query'
import { sortAvailableCoupons, sortRawCoupons } from '@features/coupon/model'
import { type CouponPublishErrorCode, useCouponPublishErrorMessages } from '@features/coupon/model'
import { BaseResponseSchema, couponResponseAdapter, GetCouponResponse, RawCoupon } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { useToast } from '@shared/hooks'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  BottomSheetContent,
  BottomSheetFooter,
  BottomSheetHeader,
  Button,
  Flex,
  Input,
  Typography,
  VStack,
} from '@podo-app/design-system-temp'

const COUPON_PUBLISH_GUIDE_MESSAGE_KEYS = [
  'coupon.register.guide.registered',
  'coupon.register.guide.accountBound',
  'coupon.register.guide.removal',
  'coupon.register.guide.prohibitedUse',
  'coupon.register.guide.cancellation',
] as const

type Props = {
  subscribeId?: string
  productId?: string | null
  purchaseOrderId?: string | null
  orderPaymentMethod?: KrPaymentOrder['paymentMethod']
  close?: () => void
  setRegisterCouponSuccessToastId?: (id: string) => void
}

const RegisterCouponBottomSheet = ({
  subscribeId,
  productId,
  purchaseOrderId,
  orderPaymentMethod,
  close,
  setRegisterCouponSuccessToastId,
}: Props) => {
  const queryClient = useQueryClient()
  const authorization = useAuthorization()
  const { toast } = useToast()
  const t = useTranslation()
  const [couponCode, setCouponCode] = useState('')
  const [error, setError] = useState<{ isError: false } | { isError: true; message: string }>({ isError: false })
  const { COUPON_PUBLISH_ERROR_MESSAGES } = useCouponPublishErrorMessages()

  const couponPublish = useMutation({
    ...couponEntityMutations.publishCoupon({ bearerToken: authorization.bearerToken, subscribeId }),
    onSuccess: async (response) => {
      const publishedCoupons = Array.isArray(response) ? response : [response]

      const { id: registerCouponSuccessToastId } = toast({ title: t('coupon.register.success') })
      setRegisterCouponSuccessToastId?.(registerCouponSuccessToastId)
      setError({ isError: false })

      queryClient.setQueryData<GetCouponResponse[]>(
        couponEntityQueries.availableCoupons({ bearerToken: authorization.bearerToken }).queryKey,
        (prevCoupons) => {
          if (!prevCoupons) {
            return prevCoupons
          }

          return sortAvailableCoupons([...prevCoupons, ...publishedCoupons.map(couponResponseAdapter)])
        },
      )

      if (subscribeId) {
        const paymentCouponQueryKey = paymentEntityQueries.getCouponForPayment({
          bearerToken: authorization.bearerToken,
          subscribeId,
          productId,
          purchaseOrderId,
          paymentMethod: orderPaymentMethod,
        }).queryKey

        if (productId) {
          await queryClient.invalidateQueries({ queryKey: paymentCouponQueryKey })
        } else {
          queryClient.setQueryData<RawCoupon[]>(paymentCouponQueryKey, (prevCoupons) => {
            if (!prevCoupons) {
              return prevCoupons
            }

            return sortRawCoupons([...prevCoupons, ...publishedCoupons])
          })
        }
      }

      onClose()
    },
    onError: (error) => {
      const cause = error.cause as BaseResponseSchema<null, CouponPublishErrorCode>

      setError({ isError: true, message: COUPON_PUBLISH_ERROR_MESSAGES[cause.resultCdName] })
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    await couponPublish.mutateAsync({ couponCode })
  }

  const onClose = () => {
    setCouponCode('')
    setError({ isError: false })
    close?.()
  }

  return (
    <BottomSheetContent>
      <VStack as="form" onSubmit={handleSubmit} className="h-full justify-between">
        <BottomSheetHeader className="flex flex-col gap-5">
          <Typography size="h1">{t('coupon.register.title')}</Typography>

          <Flex direction="vertical" className="gap-2">
            <Input
              placeholder={t('coupon.register.placeholder')}
              className="w-full"
              name="couponCode"
              required
              value={couponCode}
              onPaste={(event) => {
                event.preventDefault()
                setCouponCode(event.clipboardData.getData('text'))
                setError({ isError: false })
              }}
              onChange={(event) => {
                setCouponCode(event.target.value)
                setError({ isError: false })
              }}
            />

            {error.isError && (
              <ol className="list-disc list-outside pl-5 marker:text-red-normal">
                <li>
                  <Typography size="body1" className="text-red-normal inline">
                    {error.message}
                  </Typography>
                </li>
              </ol>
            )}
          </Flex>

          <Flex as="ol" direction="vertical" className="gap-2 list-disc list-outside pl-5 marker:text-gray-400">
            {COUPON_PUBLISH_GUIDE_MESSAGE_KEYS.map((messageKey) => (
              <li key={messageKey}>
                <Typography size="body1" className="text-gray-400 inline">
                  {t(messageKey)}
                </Typography>
              </li>
            ))}
          </Flex>
        </BottomSheetHeader>

        <BottomSheetFooter>
          <Button
            className="w-full"
            type="submit"
            disabled={error.isError || couponPublish.isPending || couponCode.length === 0}
          >
            {t('coupon.actions.submitRegister')}
          </Button>
        </BottomSheetFooter>
      </VStack>
    </BottomSheetContent>
  )
}

export default RegisterCouponBottomSheet
